import { Component, OnInit, Inject, LOCALE_ID } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { NgForm } from "@angular/forms";

import { CustomerType } from "../../shared/models/customer.type.interface";
import { User } from "../../shared/models/user.interface";
import { QuoteService } from "../../quote/services/quote.service";
import { JobType } from "../../shared/models/job.type.interface";
import { Customer } from "../../shared/models/customer.interface";
import { MonthType } from "../../shared/models/month.type.interface";
import { BidLog, BidLogResult, BidLogQuoteDetail } from "../models/bid.log.interface";
import { ReportService } from "../services/report.service";
import { FileService } from "../../shared/services/file.service";
import { DeviceDetectorService } from "ngx-device-detector";
import { AbstractReportComponent } from "../abstract.report.component";
import { RolePermission } from "../../shared/models/role.permission.enum";

export interface BidLogParameterData {
  bidLog: BidLog;

  salespersons: User[];
  jobTypes: JobType[];
  customerTypes: CustomerType[];
  customers: Customer[];
  years: number[];
  months: MonthType[];
  showMargins: boolean;
}

@Component({
  selector: 'app-bid-log',
  templateUrl: './bid.log.component.html',
  styleUrls: ['./bid.log.component.scss']
})
export class BidLogComponent extends AbstractReportComponent implements OnInit {

  report: BidLogResult = null;
  parameters: BidLog = {
    salespersonId: null,
    statusType: null,
    jobType_ID: null,
    customerType_ID: null,
    customer_ID: null,
    year: (new Date()).getFullYear(),
    startMonth: 0,
    endMonth: 11
  } as BidLog;

  salesperson: User;
  quoteStatus: string;
  jobType: JobType;
  customerType: CustomerType;
  customer: Customer;

  customerTypes: CustomerType[];
  jobTypes: JobType[];
  salespersons: User[];
  customers: Customer[];
  quoteStatuses: Record<string, string> = {};

  isSummaryLoading: boolean;
  bidLogSummary: any;
  showMargins: boolean = false;

  constructor(@Inject(LOCALE_ID) locale: string,
    deviceDetectorService: DeviceDetectorService, dialog: MatDialog, private quoteService: QuoteService, private fileService: FileService,
    private reportService: ReportService) {
    super(locale, dialog, deviceDetectorService);
  }

  ngOnInit() {
    this.showMargins = QuoteService.hasPermission(RolePermission.ViewEditMargins);

    this.quoteService.getCustomerTypes()
      .subscribe({
        next: (data) => {
          this.customerTypes = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getJobTypes()
      .subscribe({
        next: (data) => {
          this.jobTypes = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getSalespersons()
      .subscribe({
        next: (data) => {
          this.salespersons = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getCustomers()
      .subscribe({
        next: (data) => {
          this.customers = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteStatuses['A'] = 'Active';
    this.quoteStatuses['E'] = 'Expired';
    this.quoteStatuses['L'] = 'Lost';
    this.quoteStatuses['W'] = 'Won';

    this.setupDates();
  }

  renderReport() {
    this.getReportFormat()
      .subscribe(formatData => {
        if (formatData == null) return;

        this.isRequesting = true;
        this.parameters.showMargins = this.showMargins;

        this.reportService.getBidLogExport(this.parameters, formatData.format, formatData.locale)
          .subscribe({
            next: (data) => {
              this.isRequesting = false;
              this.fileService.downloadFile(data, 'BidLog' + formatData.extension);
            },
            error: (error: string) => this.onError(error)
          });
      });
  }

  showParameters() {
    // wait until dropdowns loaded
    if (this.salespersons == null ||
      this.jobTypes == null ||
      this.customerTypes == null ||
      this.customers == null)
      return;

    const dialogRef = this.dialog.open(BidLogParameterDialog, {
      width: '550px',
      data: {
        bidLog: (<any>Object).assign({}, this.parameters),

        salespersons: this.salespersons,
        jobTypes: this.jobTypes,
        customerTypes: this.customerTypes,
        customers: this.customers,
        years: this.years,
        months: this.months,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.parameters = result.bidLog as BidLog;
        this.reportService.setupParameters(this.parameters);

        this.salesperson = this.salespersons.find(s => s.id == this.parameters.salespersonId);
        this.quoteStatus = this.parameters.statusType == null ? null : this.quoteStatuses[this.parameters.statusType];
        this.jobType = this.jobTypes.find(s => s.jobType_ID == this.parameters.jobType_ID);
        this.customerType = this.customerTypes.find(s => s.customerType_ID == this.parameters.customerType_ID);
        this.customer = this.customers.find(s => s.customer_ID == this.parameters.customer_ID);

        this.isSummaryLoading = true;
        this.reportService.getBidLogReport(this.parameters)
          .subscribe({
            next: (data) => {
              this.report = this.aggregateResults(data, this.parameters.startMonth, this.parameters.endMonth);
            },
            error: (error: string) => this.onError(error)
          });
        this.reportService.getBidLogSummary(this.parameters, this.locale)
          .subscribe({
            next: (data) => {
              this.createImageFromBlob(data);
              this.isSummaryLoading = false;
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  aggregateResults(data: BidLogResult, startMonth: number, endMonth: number): BidLogResult {
    data.aggregates = new Array();

    data.quote_BidAmount = 0;
    data.totalCost = 0;
    data.totalBidAmount = 0;


    for (let i = startMonth; i <= endMonth; i++) {
      let aggregate = {
        category: this.months[i].MonthName,
        quote_BidAmount: 0,
        totalCost: 0,
        quoteMargin: 0,
        totalBidAmount: 0,
        details: new Array()
      } as BidLogQuoteDetail;
      data.aggregates.push(aggregate);

      for (var quoteDetail of data.quoteDetails) {
        let date = new Date(quoteDetail.quote_Created);
        if (date.getMonth() == i) {
          aggregate.details.push(quoteDetail);
          aggregate.quote_BidAmount += quoteDetail.quote_BidAmount;
          aggregate.totalCost += quoteDetail.totalCost;
          aggregate.totalBidAmount += quoteDetail.totalBidAmount;
        }
      }

      data.quote_BidAmount += aggregate.quote_BidAmount;
      data.totalCost += aggregate.totalCost;
      data.totalBidAmount += aggregate.totalBidAmount;

      if (aggregate.totalCost && aggregate.quote_BidAmount)
        aggregate.quoteMargin = (1 - (aggregate.totalCost / aggregate.quote_BidAmount)) * 100;
    }

    if (data.totalCost && data.quote_BidAmount)
      data.quoteMargin = (1 - (data.totalCost / data.quote_BidAmount)) * 100;

    return data;
  }

  createImageFromBlob(image: Blob): void {
    let reader = new FileReader();
    reader.addEventListener("load", () => {
      this.bidLogSummary = reader.result;
    }, false);

    if (image) {
      reader.readAsDataURL(image);
    }
  }
}

@Component({
  selector: 'bid.log.parameter.dialog',
  templateUrl: 'bid.log.parameter.dialog.html',
})
export class BidLogParameterDialog {

  constructor(
    public dialogRef: MatDialogRef<BidLogParameterDialog>,
    @Inject(MAT_DIALOG_DATA) public data: BidLogParameterData) { }

  runReport(form: NgForm): void {
    if (form.valid) {
      this.dialogRef.close(this.data);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
